import { useEffect } from 'react';
import type {
  UseFormClearErrors,
  UseFormSetError,
  FieldValues,
  Path,
} from 'react-hook-form';
import type { KinnserClinicDataResponse } from '../../services/api/types.gen';

interface UseMrnValidationOptions<TFieldValues extends FieldValues> {
  clinicData: KinnserClinicDataResponse | null;
  loadingClinicData: boolean;
  mrnValue: string | null | undefined;
  clearErrors: UseFormClearErrors<TFieldValues>;
  setError: UseFormSetError<TFieldValues>;
}

/**
 * Hook for conditionally validating MRN based on clinic's auto_mrn setting.
 * When auto_mrn is true, MRN validation is disabled.
 * When auto_mrn is false, MRN is required.
 */
export function useMrnValidation<TFieldValues extends FieldValues>({
  clinicData,
  loadingClinicData,
  mrnValue,
  clearErrors,
  setError,
}: UseMrnValidationOptions<TFieldValues>): void {
  useEffect(() => {
    if (loadingClinicData) return; // Wait for clinic data to load

    if (clinicData?.auto_mrn) {
      // When auto_mrn is true, clear any MRN validation errors
      clearErrors('kinnser_mrn' as Path<TFieldValues>);
    } else {
      // When auto_mrn is false, MRN is required
      if (
        !mrnValue ||
        (typeof mrnValue === 'string' && mrnValue.trim().length === 0)
      ) {
        setError('kinnser_mrn' as Path<TFieldValues>, {
          type: 'manual',
          message: 'MRN is required',
        });
      } else {
        clearErrors('kinnser_mrn' as Path<TFieldValues>);
      }
    }
  }, [
    clinicData?.auto_mrn,
    mrnValue,
    loadingClinicData,
    clearErrors,
    setError,
  ]);
}
